<template>
  <section id="cartSuccess">
    <!-- ===== PER-ORDER PRINT-ONLY HEADERS =====
         The letterhead (logo + Parts Department address) used
         to live here as a single page-level banner. With
         multi-order print support, each Order # group now
         carries its own copy of the header inside the
         v-for("group in printOrderGroups") loop further down,
         and a `page-break-before: always` rule on subsequent
         groups gives every order its own page in print. The
         per-order header markup is identical to what used to
         render here, just relocated. See the
         `.print-only-header` block inside the v-for and the
         @media print rules at the bottom of this file. -->

    <!-- The screen-rendered logo error fallback flag is
         consumed by every per-order header instance below
         (single piece of state, multiple consumers). -->

    <div id="breadcrumb">
      <div class="container">
        <ul>
          <li><router-link to="/" tag="a">Home</router-link></li>
          <li class="last">
            <router-link to="#" tag="a">Order History</router-link>
          </li>
        </ul>
      </div>
    </div>
    <div id="finalizeTop">
      <div class="container">
        <div class="row mb-5">
          <!-- Left column shrunk from col-lg-8 to col-lg-7 to give
               the Frequently Ordered upsell on the right (col-lg-5)
               more horizontal room. The card holds part codes
               (e.g. "DSD S 0051 DU"), descriptions, and the
               "Ordered N times · M qty" footer all on the same
               row — at col-lg-4 the description was wrapping onto
               4-5 lines because there wasn't enough width for the
               text + button. col-lg-5 gives the row enough room
               to lay out cleanly. -->
          <div class="col-lg-7">
            <div class="row mb-4">
              <div class="col-12">
                <h1 class="green">Order Successfully Placed</h1>
                <p>
                  Please reference the Acknowledgement Number below if you need
                  to contact customer service.
                </p>
                <p>
                  Once we review your order you will receive a confirmation
                  acknowledgement with an Order Number.
                </p>
              </div>
            </div>

            <div class="row">
              <div class="col-12">
                <h2>Acknowledgement No: {{ orderId }}</h2>
              </div>
            </div>

            <!-- ===== INFO CARDS — Sold To + Ship To =====
                 Replaces the previous green/dark gradient
                 colored-bar headers with a clean two-card
                 layout that matches OrderDetails.vue. White
                 background, subtle border, small red top
                 accent, dashed header divider. flex via
                 .info-cards-row gives equal width with a
                 min-width that keeps them readable; flex-wrap
                 stacks them on narrow screens.

                 Markup + scoped styles ported from
                 OrderDetails.vue so a customer reviewing a
                 placed order on Success and then viewing it
                 later on Order Details sees the same address
                 treatment in both places. -->
            <div class="info-cards-row">
              <div class="info-card">
                <div class="info-card-header">Sold To</div>
                <div class="info-card-body">
                  <div class="info-card-name">{{ companyName }}</div>
                  <p v-if="billingStreet">{{ billingStreet }}</p>
                  <p v-if="billingStreet2">{{ billingStreet2 }}</p>
                  <p>
                    {{ billingCity }}, {{ billingState }} {{ billingPostalCode }}
                  </p>
                  <p v-if="name">{{ name }}</p>
                  <p v-if="phone">{{ phone }}</p>
                </div>
              </div>
              <div class="info-card">
                <div class="info-card-header">Ship To</div>
                <div class="info-card-body">
                  <div class="info-card-name">{{ companyName }}</div>
                  <p v-if="shippingStreet">{{ shippingStreet }}</p>
                  <p v-if="shippingStreet2">{{ shippingStreet2 }}</p>
                  <p>
                    {{ shippingCity }}, {{ shippingState }} {{ shippingPostalCode }}
                  </p>
                  <p v-if="name">{{ name }}</p>
                  <p v-if="phone">{{ phone }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="col-lg-5">
            <div id="cartSidebar">

              <!-- Quick-action fallbacks — kept underneath the
                   upsell card so the user can still jump to
                   Order Status / Create flows directly without
                   picking a specific part. All three buttons use
                   btn-primary (red+white) for visual consistency
                   — they're peer actions and shouldn't compete
                   with each other for prominence. -->
              <div class="upsell-quick-actions"
                   v-if="upsellCompanyId">
                <router-link to="/account/savedcarts?tab=orders"
                             tag="a"
                             class="btn btn-primary upsell-quick-btn">
                  Check Order Status
                </router-link>
                <router-link to="/orders/create"
                             tag="a"
                             class="btn btn-primary upsell-quick-btn">
                  Create New Order
                </router-link>
                <router-link to="/rfq/create"
                             tag="a"
                             class="btn btn-primary upsell-quick-btn">
                  Create New Quote
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== PRINT ORDER ACTION =====
         Centered Print button in its own breathing-room band
         between the info cards / upsell column and the PO
         Order details below. Opens the Print Order(s) modal —
         see #print-order-modal below. The modal lets the user
         confirm the action and, when more than one Order
         (PO group) is present, pick which subset to print.
         The modal's confirm handler is what actually calls
         window.print() (the user picks "Save as PDF" from the
         destination dropdown if they want a PDF). The
         @media print stylesheet hides the site chrome, the
         upsell panel, the quick-action buttons, and this
         section itself so the printout is just the receipt. -->
    <div class="print-order-band no-print">
      <div class="container">
        <button type="button"
                class="btn btn-outline-dark btn-print-order"
                @click="openPrintModal">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
               class="btn-print-icon">
            <polyline points="6 9 6 2 18 2 18 9"></polyline>
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
            <rect x="6" y="14" width="12" height="8"></rect>
          </svg>
          {{ printButtonLabel }}
        </button>
      </div>
    </div>

    <b-form id="assemblyForm">
      <!-- ===== PER-ORDER SECTIONS =====
           printOrderGroups buckets the flat displayedOrders rows
           by purchaseOrderNumber so each Order # is a single
           wrapped section. The section is what the print
           stylesheet uses as a hook to:
             1. Insert a `page-break-before: always` between
                consecutive sections — every order starts on its
                own page in the printed output, with its own
                letterhead at the top.
             2. Hide a section entirely when the user picked a
                subset to print (printingSelectedOnly flag set,
                this group's number not in
                selectedOrdersForPrint). The
                `print-order-hidden` class drives that via an
                @media print { display: none } rule.
           Outside print (default screen view) the wrapper is
           a transparent passthrough — no visual change. The
           inner v-for over group.assemblies preserves the
           legacy per-machine/assembly card layout exactly,
           including the isFirstInPoGroup-conditional PO
           banner. -->
      <section class="print-order-section"
               v-for="(group, gIdx) in printOrderGroups"
               :key="'po-section-' + group.number"
               :class="{ 'print-order-hidden': isOrderHiddenForPrint(group.number) }">
        <!-- ===== PER-ORDER PRINT-ONLY LETTERHEAD =====
             Identical markup and behavior to the legacy
             page-level header, just relocated so each order
             carries its own copy. Hidden on screen via the
             scoped .print-only-header { display: none } rule;
             revealed in print by the @media print { display:
             flex } rule at the bottom of the file.

             Two logo strategies stacked: <img> first (same
             SVG used by Head.vue), text "STEELE" fallback if
             the SVG fails to load. printLogoFailed is shared
             across every header instance — one failed load
             flips the flag for all of them, which is the
             correct behavior since they all reference the
             same asset. -->
        <div class="print-only-header">
          <div class="print-only-header-logo">
            <img src="@/assets/img/steele-logo.svg"
                 alt="JC Steele"
                 class="print-only-header-logo-img"
                 @error="onPrintLogoError"
                 v-if="!printLogoFailed" />
            <div class="print-only-header-logo-text"
                 v-if="printLogoFailed">
              STEELE
            </div>
          </div>
          <div class="print-only-header-address">
            <!-- Doc identifier (Order #, Quote #, or
                 Acknowledgement #) — print-only, renders at
                 the top of every printed page so the
                 recipient can tie the output back to a
                 specific record. -->
            <div class="print-only-header-doc-id">Acknowledgement # {{ orderId }}</div>
            <div class="print-only-header-address-name">Parts Department</div>
            <div>145 National Dr.</div>
            <div>Statesville, NC 28625</div>
            <div>P. (800) 278-3353</div>
          </div>
        </div>

        <div class="POOrders" v-for="(order, oIdx) in group.assemblies" :key="'asm-' + group.number + '-' + oIdx">
          <div class="container">
            <!-- PO NUMBER header — rendered only on the first
                 row of a PO group. Subsequent assemblies in the
                 same Order silently inherit the PO above them
                 (the visual gap between assembly cards within
                 the same Order is smaller than between Orders,
                 which the .POOrders + container CSS already
                 handles). With the new per-order section
                 wrapping, oIdx === 0 within each group is the
                 canonical "first row" marker; the legacy
                 isFirstInPoGroup flag from displayedOrders
                 still works (and is preserved on the row) but
                 oIdx === 0 is the more direct expression now
                 that grouping is at the v-for level. -->
            <div class="row mb-3" v-if="oIdx === 0">
              <div class="col-lg-8">
                <div class="ponumber">
                  PO NUMBER: {{ order.purchaseOrderNumber }}
                </div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-12">
                <div class="assemblyName">{{ order.machineDescription }}</div>
                <div class="assemblyDescription">{{ order.assemblyName }}</div>
              </div>
            </div>

            <!-- stacked="sm" (not "lg"): print paper width (~816px for
                 US Letter) falls BETWEEN the sm (576px) and lg (992px)
                 breakpoints, so "lg" rendered this table in stacked
                 (per-row label/value block) mode when printing — which
                 is what threw the column headers out of alignment with
                 their values. "sm" keeps it a normal, column-aligned
                 table at print width, matching the SavedRFQ.vue order
                 print that renders correctly. -->
            <b-table
              stacked="sm"
              :fields="partsFields"
              :items="order.parts"
              responsive="sm"
            >
              <!-- A virtual column -->
              <template v-slot:cell(item)="data">
                <div class="nowrap">
                  {{ data.item.partItem }}
                </div>
              </template>

              <!-- A custom formatted column -->
              <template v-slot:cell(code)="data">
                <div class="nowrap">
                  {{ data.item.partCode }}
                </div>
              </template>

              <!-- A virtual composite column -->
              <template v-slot:cell(description)="data">
                {{ data.item.partDescription }}
              </template>

              <!-- Optional default data cell scoped slot -->
              <template v-slot:cell(qty)="data">
                {{ data.item.partQuantityPerMachine }}
              </template>

              <template v-slot:cell(price)="data">
                {{ data.item.partPrice | currency }}
              </template>

              <template v-slot:cell(cost)="data">
                {{ data.item.cost | currency }}
              </template>
            </b-table>

            <div class="form-btns">
              <div class="row">
                <div class="col-lg-8 offset-lg-4">
                  <div class="row">
                    <div class="col-lg-8 mb-4 text-right pr-lg-0">
                      <div class="subtotal-label">
                        Subtotal
                      </div>
                    </div>
                    <div class="col-lg-4 mb-4 pl-lg-0">
                      <div class="subtotal-price">
                        {{ order.cost | currency }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== ORDER GRAND TOTAL =====
           Replaces the previous full-viewport-width image
           banner with the same clean panel used by
           OrderDetails and Cart: heavy black top border,
           centered uppercase label, large red total. The
           customer sees the same one-line summary they had
           at checkout and that they'll see when reviewing
           later on /account/orderdetails. Markup + scoped
           styles ported from OrderDetails.vue.

           no-print is bound conditionally based on
           printOrderGroups.length:
             - 1 order  → cross-document grand total prints
                          (it's the same as the only order's
                          subtotal anyway).
             - N orders → cross-document grand total is
                          suppressed in print. Each printed
                          page already shows that order's
                          own subtotal at the bottom (the
                          per-assembly Subtotal block), so a
                          "Total for All Orders" trailing
                          the last printed page would just
                          confuse rather than inform. The
                          total stays visible on screen —
                          only the print output loses it. -->
      <div class="container">
        <div class="cart-grand-total"
             :class="{ 'no-print': printOrderGroups.length > 1 }">
          <div class="cart-grand-total-label">Total for All Orders</div>
          <div class="cart-grand-total-value">{{ total | currency }}</div>
        </div>
      </div>
    </b-form>

    <!-- ===== PRINT ORDER(S) MODAL =====
         Opens when the user clicks the Print Order(s) button
         in the .print-order-band above. Always shown on click —
         even for the single-order case — so the user has a
         consistent confirmation step that mirrors the
         Print Quote(s) modals on SavedRFQ / RFQDetails and
         the Print Order modal on OrderDetails.

         Two flavors driven off printOrderGroups.length:
           1 order   — banner reads "Ready to Print Your Order?",
                       no checkboxes (one row, always selected),
                       primary button reads "Print Order".
           N orders  — banner reads "Ready to Print Orders?",
                       a checkbox per Order # so the user can
                       deselect ones they don't want to print,
                       primary button reads "Print All Orders"
                       when everything's selected, "Print N
                       Selected Orders" when a subset is.

         no-print on the modal-class so an accidentally-open
         dialog doesn't bleed into the print output. -->
    <b-modal id="print-order-modal"
             v-model="showPrintModal"
             :title="printModalTitle"
             size="md"
             centered
             modal-class="print-order-modal no-print"
             :ok-title="printModalConfirmLabel"
             ok-variant="primary"
             cancel-title="Cancel"
             cancel-variant="outline-secondary"
             :ok-disabled="!hasSelectedOrdersForPrint"
             @ok.prevent="confirmPrint">

      <!-- Brand-red banner — same visual treatment as the
           Print Quote / Finalize / Modify Quote modals so
           the order/quote flows feel consistent. Title varies
           by single vs multi-order case (see
           printModalBannerTitle computed). -->
      <div class="modify-restored-banner print-order-banner">
        <div class="modify-restored-title">
          {{ printModalBannerTitle }}
        </div>
        <div class="modify-restored-body">
          <span v-if="printOrderGroups.length === 1">
            Confirm below — then click <strong>Print Order</strong>
            to open the printable order receipt.
          </span>
          <span v-else>
            Confirm the POs below — then click
            <strong>{{ printModalConfirmLabel }}</strong> to open
            the printable order receipt. Each selected PO
            prints on its own page with the JC Steele
            letterhead at the top.
          </span>
        </div>
      </div>

      <p class="convert-modal-intro" v-if="printOrderGroups.length > 1">
        Select which POs you want to include on the printed
        output.
      </p>

      <!-- Order rows. For a single-PO order we still render
           the row (just without a selection checkbox) so the
           user sees what they're about to print. For multiple
           POs, each row gets a b-form-checkbox bound by the
           PO number — same container/styling vocabulary
           (.convert-quote-list, .convert-quote-row,
           .convert-quote-row-grouped, etc.) used by the
           Finalize / Save for Later picker on
           /cart/rfqsaveditems so the pages feel like
           siblings.

           Per-row content mirrors the on-page layout:
           PO #{number}, assembly count, total cost, and a
           small list of each constituent assembly. -->
      <div class="convert-quote-list">
        <div v-for="(group, gIdx) in printOrderGroups"
             :key="'print-pick-' + group.number"
             class="convert-quote-row convert-quote-row-grouped">
          <!-- Multi-PO: bound checkbox per row. -->
          <b-form-checkbox v-if="printOrderGroups.length > 1"
                           :checked="isOrderSelectedForPrint(group.number)"
                           @change="setOrderSelectionForPrint(group.number, $event)">
            <div class="convert-quote-row-main">
              <strong class="convert-quote-row-label">
                PO #{{ group.number }}
              </strong>
            </div>
            <div class="convert-quote-row-sub">
              {{ group.assemblies.length }}
              {{ group.assemblies.length === 1 ? 'assembly' : 'assemblies' }}
              <span class="convert-quote-row-amount"
                    v-if="group.cost > 0">
                · {{ group.cost | currency }}
              </span>
            </div>
            <div class="convert-quote-row-assemblies"
                 v-if="group.assemblies.length > 0">
              <div v-for="(asm, aIdx) in group.assemblies"
                   :key="'print-pick-asm-' + group.number + '-' + aIdx"
                   class="convert-quote-row-assembly">
                <span v-if="asm.machineDescription">{{ asm.machineDescription }}</span>
                <template v-if="asm.assemblyName"> · {{ asm.assemblyName }}</template>
                <template v-if="asm.parts && asm.parts.length">
                  · {{ asm.parts.length }}
                  {{ asm.parts.length === 1 ? 'part' : 'parts' }}
                </template>
              </div>
            </div>
          </b-form-checkbox>

          <!-- Single-PO: no checkbox, just the same row
               layout as the multi-PO case minus the form
               control. .print-order-row-static is the
               wrapper that replaces b-form-checkbox in
               that branch. -->
          <div v-else class="print-order-row-static">
            <div class="convert-quote-row-main">
              <strong class="convert-quote-row-label">
                PO #{{ group.number }}
              </strong>
            </div>
            <div class="convert-quote-row-sub">
              {{ group.assemblies.length }}
              {{ group.assemblies.length === 1 ? 'assembly' : 'assemblies' }}
              <span class="convert-quote-row-amount"
                    v-if="group.cost > 0">
                · {{ group.cost | currency }}
              </span>
            </div>
            <div class="convert-quote-row-assemblies"
                 v-if="group.assemblies.length > 0">
              <div v-for="(asm, aIdx) in group.assemblies"
                   :key="'print-static-asm-' + group.number + '-' + aIdx"
                   class="convert-quote-row-assembly">
                <span v-if="asm.machineDescription">{{ asm.machineDescription }}</span>
                <template v-if="asm.assemblyName"> · {{ asm.assemblyName }}</template>
                <template v-if="asm.parts && asm.parts.length">
                  · {{ asm.parts.length }}
                  {{ asm.parts.length === 1 ? 'part' : 'parts' }}
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Select All / Deselect All toggle — only when
           there's more than one order (single-order case has
           no checkbox to flip anyway). Mirrors the Finalize
           modal on /cart/rfqsaveditems for visual
           consistency. -->
      <div class="convert-quote-toggle-all"
           v-if="printOrderGroups.length > 1">
        <a href="#"
           @click.prevent="toggleSelectAllOrdersForPrint">
          {{ selectedOrdersForPrint.length === printOrderGroups.length
              ? 'Deselect All'
              : 'Select All' }}
        </a>
      </div>
    </b-modal>
  </section>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";
import { cleanupTempSetupAdds } from "@/assets/js/tempSetupAdds";
const apiService = new FeApiService();

import "expose-loader?$!expose-loader?jQuery!jquery";
import "semantic-ui-dropdown/dropdown.js";

import Vue from "vue";
import Vue2Filters from "vue2-filters";

Vue.use(Vue2Filters);

export default {
  name: "Cart",
  computed: {
    // Set of partCodes that appeared in the just-completed
    // order — used to exclude them from the Frequently
    // Ordered upsell list so we don't suggest "buy more of
    // exactly what you just bought" right after checkout.
    // Built from this.orders (populated async by
    // getSuccessOrder); empty until that resolves, which
    // is fine — the filtered upsell list will rebuild
    // reactively once the order data arrives.
    justOrderedPartCodes() {
      var codes = {};
      (this.orders || []).forEach(function (order) {
        (order.parts || []).forEach(function (p) {
          if (p && p.partCode) {
            codes[p.partCode] = true;
          }
        });
      });
      return codes;
    },

    // Frequently Ordered minus parts the user just placed in
    // this order. Mirrors how Search.vue's filteredUpsellParts
    // excludes parts already shown in the active search.
    filteredUpsellParts() {
      var exclude = this.justOrderedPartCodes;
      return (this.upsellParts || []).filter(function (p) {
        return !exclude[p.partCode];
      });
    },

    // Sliced view of filteredUpsellParts for "See more" pagination.
    // Mirrors the same pattern on Index_-_Search.vue.
    displayedUpsellParts() {
      return this.filteredUpsellParts.slice(0, this.upsellLimit);
    },

    // ===== PER-PO HEADER COLLAPSING =====
    // The placed-order response returns one row per machine +
    // assembly combination, and a single Order with multiple
    // assemblies (e.g. "Order #1: WEDGE GROOVE LINER + CORE
    // PIN FOR K DIE + None") shows up here as N rows that
    // share the same purchaseOrderNumber. Repeating the
    // "PO NUMBER: 1" banner above every row reads as if
    // they're separate orders. Walk the list once and flag
    // each row's position within its PO group so the template
    // can render the banner only on the first row.
    //
    // The placed-order rows already arrive grouped by PO
    // (CartManager's projection orders by purchaseOrderId),
    // so a simple "is the previous row's PO the same?" check
    // is sufficient — no need to re-sort.
    displayedOrders() {
      var rows = this.orders || [];
      var prevPo = null;
      return rows.map(function (o) {
        var po = o.purchaseOrderNumber;
        var isFirst = po !== prevPo;
        prevPo = po;
        return Object.assign({}, o, { isFirstInPoGroup: isFirst });
      });
    },

    // ===== PRINT GROUPING =====
    // Buckets displayedOrders by purchaseOrderNumber so the
    // template's outer v-for can wrap each Order # in its own
    // .print-order-section (which gets a per-order letterhead
    // and a page-break-before in print). Each group carries:
    //   - number     PO number (the matching key for the
    //                modal's selectedOrdersForPrint set).
    //   - cost       Sum of constituent assembly costs, used
    //                in the modal row's right-side amount.
    //   - assemblies Ordered list of the original
    //                displayedOrders rows that share this PO.
    //                Each row keeps its full original shape
    //                (machineDescription, assemblyName,
    //                parts, cost, isFirstInPoGroup, etc.) so
    //                the inner v-for renders identically to
    //                the legacy flat layout.
    //
    // Order is preserved from displayedOrders, which already
    // arrives PO-grouped (CartManager's projection orders by
    // purchaseOrderId), so simple iteration suffices — no
    // re-sort needed.
    printOrderGroups() {
      var groups = [];
      var byPo = {};
      (this.displayedOrders || []).forEach(function (o) {
        var po = o.purchaseOrderNumber || '';
        if (!byPo[po]) {
          byPo[po] = {
            number: po,
            cost: 0,
            assemblies: [],
          };
          groups.push(byPo[po]);
        }
        byPo[po].assemblies.push(o);
        byPo[po].cost += Number(o.cost) || 0;
      });
      return groups;
    },

    // ===== PRINT BUTTON / MODAL TEXT (singular vs plural) =====
    // Drives the visible label on the .btn-print-order
    // button and the modal title. Singular / plural based
    // purely on printOrderGroups.length so a one-PO order
    // reads naturally instead of "Print Orders" with one
    // item.
    printButtonLabel() {
      return this.printOrderGroups.length > 1 ? "Print Orders" : "Print Order";
    },
    printModalTitle() {
      return this.printOrderGroups.length > 1 ? "Print Orders" : "Print Order";
    },

    // ===== BANNER TITLE =====
    // The big red callout at the top of the modal. Singular
    // form for the one-order case — "Ready to Print Your
    // Order?". Plural form covers the multi-PO case (which
    // shows up here when the placed order spanned multiple
    // POs in one cart submission).
    printModalBannerTitle() {
      return this.printOrderGroups.length > 1
        ? "Ready to Print Orders?"
        : "Ready to Print Your Order?";
    },

    // ===== CONFIRM BUTTON LABEL =====
    // Three flavors:
    //   - Single PO                              → "Print Order"
    //   - Multi PO, all selected                 → "Print All POs"
    //   - Multi PO, partial selection            → "Print N Selected POs"
    // Each printed page is one PO; the confirm button states
    // exactly what will land on paper. The single-PO case
    // keeps the friendlier "Print Order" framing (the order
    // is its one PO) — only the multi-PO selection cases need
    // the precise PO terminology to tell the user how many
    // pages they'll get.
    printModalConfirmLabel() {
      var total = this.printOrderGroups.length;
      if (total <= 1) return "Print Order";
      var picked = this.selectedOrdersForPrint.length;
      if (picked === 0) return "Print";
      if (picked === total) return "Print All POs";
      return "Print " + picked + " Selected " +
        (picked === 1 ? "PO" : "POs");
    },

    // ===== CONFIRM BUTTON ENABLE STATE =====
    // Disable Print when nothing is checked. Single-order
    // case is always enabled because there's nothing to
    // uncheck (the row renders without a checkbox at all).
    hasSelectedOrdersForPrint() {
      if (this.printOrderGroups.length <= 1) return true;
      return this.selectedOrdersForPrint.length > 0;
    },
  },
  mounted() {
    if (localStorage.cartId == null) {
      this.$router.push("/orders/create");
    }
    this.getSuccessOrder();
    // Note: loadUpsellInsights is now called inside
    // orderSuccessful (after getSuccessOrder resolves) rather
    // than here. Calling it from mounted directly was racing
    // with localStorage updates and the place-order flow's
    // backend writes — sometimes the API returned empty
    // because the just-placed order's parts hadn't been
    // indexed yet. Chaining it after orderSuccessful
    // guarantees the order is fully recorded before we ask
    // the insights endpoint for top-N.
  },
  data() {
    return {
      orderId: "",
      configuredWear: true,
      companyName: "",
      orders: [],
      total: "",
      // Print-header logo error fallback. Flipped true by
      // onPrintLogoError when the <img> fails to load,
      // swapping the markup to a text "STEELE" fallback
      // so the printed receipt always carries a brand mark.
      printLogoFailed: false,
      billingCompanyName: "",
      billingStreet: "",
      billingStreet2: "",
      billingCity: "",
      billingState: "",
      billingPostalCode: "",
      shippingCompanyName: "",
      shippingStreet: "",
      shippingStreet2: "",
      shippingCity: "",
      shippingState: "",
      shippingPostalCode: "",
      name: "",
      phone: "",
      partsFields: [
        "item",
        "code",
        "description",
        { key: "qty", label: "QTY PER MACHINE" },
        "price",
        "cost",
      ],
      // ===== FREQUENTLY ORDERED UPSELL STATE =====
      // upsellCompanyId — read from localStorage on mount so
      // we know whose top-parts to fetch. Drives the v-if
      // gate on the upsell panel; null means no panel.
      // upsellParts — flat array from getInsights({...}).topParts
      // upsellLimit — how many rows to show; "See more"
      //   bumps it by 5 each click (matches Search.vue).
      upsellCompanyId: null,
      upsellParts: [],
      upsellLimit: 5,
      // Snapshot of the machine + assembly + company context the
      // user was working in when they placed the order. Captured
      // by orderSuccessful before localStorage gets wiped so the
      // Frequently Ordered upsell handoff (and any future "next
      // action" flows on this page) can carry that context
      // forward via URL query params. Empty strings until the
      // place-order response resolves.
      placedMachineId: "",
      placedAssemblyId: "",
      placedCompanyId: "",

      // ===== PRINT MODAL STATE =====
      // showPrintModal — drives the b-modal v-model. Flipped
      // true by openPrintModal (button click), back to false
      // by Cancel / dismiss / successful print.
      //
      // selectedOrdersForPrint — array of group.number (PO
      // number) values representing which orders will appear
      // in the print output. Populated to "all of them" when
      // the modal opens; the user can deselect rows in the
      // multi-order case. confirmPrint short-circuits if
      // this is empty (the modal's primary button is
      // disabled in that state, but we double-check).
      //
      // printingSelectedOnly — set true for the duration of
      // window.print(). The per-order section wrapper hides
      // itself in print when this flag is true AND the
      // section's group.number isn't in
      // selectedOrdersForPrint, via the print-order-hidden
      // class plumbed through isOrderHiddenForPrint(). Reset
      // to false in confirmPrint's finally so a stale flag
      // can't suppress a future full-page print.
      showPrintModal: false,
      selectedOrdersForPrint: [],
      printingSelectedOnly: false,
    };
  },
  methods: {
    // Logo <img> in the print header failed to load —
    // flip the flag so the v-if swaps to the text
    // fallback ("STEELE") visible in the next paint,
    // before the user triggers print.
    onPrintLogoError() {
      this.printLogoFailed = true;
    },

    // ===== PRINT MODAL: OPEN =====
    // Click handler for the .btn-print-order button. Opens
    // the confirmation modal (always — even for a single
    // order, so the experience is consistent across counts).
    // Pre-selects every order so "Print All" is the
    // zero-click default; the user only has to interact
    // when they want a subset.
    //
    // Edge case: printOrderGroups is empty (no orders
    // resolved yet, or createOrder failed). Surface a toast
    // and skip opening the modal — matches the same not-
    // ready-yet pattern used elsewhere.
    openPrintModal() {
      if (!this.printOrderGroups || this.printOrderGroups.length === 0) {
        this.$bvToast.toast(
          "Order data isn't ready yet — please try again in a moment.",
          {
            title: "Cannot print",
            autoHideDelay: 4000,
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      // Reset selection to "everything checked" each time
      // the modal opens so a previous partial selection
      // doesn't linger. PO numbers are the source-of-truth
      // identifier for matching against the per-order
      // print sections.
      this.selectedOrdersForPrint = this.printOrderGroups.map(function (g) {
        return g.number;
      });
      this.showPrintModal = true;
    },

    // ===== PRINT MODAL: CONFIRM =====
    // Triggered by the modal's primary button. Sets the
    // printingSelectedOnly flag (which the per-order
    // section wrapper consumes via isOrderHiddenForPrint),
    // waits for Vue to flush the class change to the DOM,
    // then fires window.print(). The browser's print dialog
    // is modal — by the time it returns we're safe to roll
    // the flag back so a subsequent unrelated print (e.g.
    // browser hotkey) shows the full page.
    //
    // Wrapped in try/finally so an exception during print
    // (rare, but possible — some embedded contexts
    // disallow it) doesn't strand the page in
    // printingSelectedOnly=true state, which would silently
    // hide order sections on future prints.
    confirmPrint() {
      var self = this;
      // Defensive — ok-disabled covers the multi-order
      // empty case, but this also gates accidental
      // programmatic calls.
      if (!this.hasSelectedOrdersForPrint) return;

      this.printingSelectedOnly = true;
      this.showPrintModal = false;

      // Wait for the section wrappers to pick up the
      // print-order-hidden class via reactivity. Without
      // nextTick, window.print() runs against the
      // pre-update DOM and unselected orders still
      // appear.
      this.$nextTick(function () {
        try {
          window.print();
        } finally {
          // Reset on the NEXT tick so any frame the
          // print engine captures during a slow dialog
          // open still has the hidden state applied.
          // window.print() is synchronous in mainstream
          // browsers, but the belt-and-braces approach
          // here costs nothing.
          self.$nextTick(function () {
            self.printingSelectedOnly = false;
          });
        }
      });
    },

    // ===== PRINT SELECTION HELPERS =====
    // isOrderSelectedForPrint — checkbox v-bind helper.
    // isOrderHiddenForPrint   — wrapper class helper.
    //                           Returns true when we're in
    //                           selected-only print mode
    //                           AND this order isn't on
    //                           the picked list. The
    //                           wrapper toggles
    //                           print-order-hidden based
    //                           on the result; the
    //                           @media print rule does the
    //                           actual hiding.
    // setOrderSelectionForPrint — checkbox @change handler.
    // toggleSelectAllOrdersForPrint — Select / Deselect
    //                                 All link at the
    //                                 bottom of the modal.
    isOrderSelectedForPrint(poNumber) {
      return this.selectedOrdersForPrint.indexOf(poNumber) !== -1;
    },
    isOrderHiddenForPrint(poNumber) {
      if (!this.printingSelectedOnly) return false;
      return !this.isOrderSelectedForPrint(poNumber);
    },
    setOrderSelectionForPrint(poNumber, checked) {
      var idx = this.selectedOrdersForPrint.indexOf(poNumber);
      if (checked && idx === -1) {
        // Push a copy so v-model bindings further up see
        // a new array reference and re-render reliably
        // (Vue 2 can miss in-place mutations of arrays
        // held on data props in some edge cases).
        this.selectedOrdersForPrint = this.selectedOrdersForPrint.concat([poNumber]);
      } else if (!checked && idx !== -1) {
        var next = this.selectedOrdersForPrint.slice();
        next.splice(idx, 1);
        this.selectedOrdersForPrint = next;
      }
    },
    toggleSelectAllOrdersForPrint() {
      // If everything is currently checked, clear;
      // otherwise select all. Mirrors the Print Quote
      // modal's same toggle.
      if (this.selectedOrdersForPrint.length === this.printOrderGroups.length) {
        this.selectedOrdersForPrint = [];
      } else {
        this.selectedOrdersForPrint = this.printOrderGroups.map(function (g) {
          return g.number;
        });
      }
    },

    getSuccessOrder() {
      apiService
        .createOrder({
          id: this.$route.params.id,
          order: this.$route.params.fullOrder,
          SpecialInstructions: this.$route.params.SpecialInstructions,
        })
        .then((request) => {
          this.orderSuccessful(request);
        })
        .catch((err) => this.orderFailed(err));
    },
    orderSuccessful(request) {
      // ===== STASH PLACED-ORDER CONTEXT BEFORE CLEARING =====
      // The user's machine + assembly selection at place-order
      // time is the natural starting context for any "next
      // action" they take from this page (Frequently Ordered
      // upsell add, Create New Order, etc.). The cleanup below
      // wipes those localStorage keys because they're tied to
      // the just-completed cart's working state — but we still
      // want them available IN-MEMORY for the handoff flows on
      // this page. Stash them on the component before delete.
      // The placed-order's response only carries
      // machineDescription (string), not machineId, so
      // localStorage is the only authoritative source for the
      // numeric ID at this point.
      this.placedMachineId = localStorage.machineId || "";
      this.placedAssemblyId = localStorage.assemblyId || "";
      this.placedCompanyId =
        (request && request.companyId)
        || localStorage.companyId
        || "";

      // ===== PARTIAL-PLACE HANDOFF =====
      // When cart.vue's Place Order picker takes its partial
      // path, it stashes the user's UNSELECTED orders in a
      // separate cart and passes that cart's id via
      // params.restoreCartId. The just-placed cart that
      // brought us to this page becomes "submitted" and is
      // cleared from localStorage in the block below — but
      // the user's expectation is that the unselected orders
      // are still in their active cart (not in Saved Carts).
      // Capture the restore target before the cleanup wipes
      // localStorage, then re-set localStorage.cartId to that
      // value after. Next visit to /cart picks up the
      // unselected orders automatically.
      //
      // Empty / missing restoreCartId is the normal full-place
      // case and falls through to the standard cleanup.
      var restoreCartId = this.$route.params.restoreCartId || "";

      delete localStorage.cartId;
      delete localStorage.poNumber;
      delete localStorage.machineId;
      delete localStorage.assemblyId;

      if (restoreCartId) {
        try { localStorage.cartId = restoreCartId; } catch (e) {}
      }

      // Clear the Search page's staging tray for this company.
      // The tray persists in localStorage under a key of the
      // form `searchStaging_<companyId>` so it survives across
      // navigations between Search and other pages. After the
      // order is placed, the cart it mirrored is gone — leaving
      // the tray populated would mislead the user into thinking
      // the parts they just ordered are still pending. Clearing
      // it here keeps Search's tray in sync with the actual
      // cart state.
      //
      // Same companyId is used in Search.vue's hydration logic
      // (line ~1227 of Index_-_Search.vue), so removing this
      // key is what Search.vue reads on its next mount.
      try {
        if (request && request.companyId) {
          localStorage.removeItem("searchStaging_" + request.companyId);
        }
      } catch (e) {
        // localStorage can throw in private browsing modes;
        // a stale tray is preferable to a thrown exception.
      }

      // Notify the header so the View Cart badge re-fetches.
      // Without this, the count stays at whatever it was
      // before place-order until the user navigates somewhere
      // else (Head.vue persists across SPA route changes
      // because it lives outside <router-view>, so its
      // mounted() hook only fires on full page load).
      // The bus is set up by Head.vue itself on
      // Vue.prototype.$cartBus during its own mount.
      if (this.$cartBus) {
        this.$cartBus.$emit("cart-updated");
      }
      // Order placed → send @jcsteele.com / non-Customer staff back to their NC999 home (Head handles the role
      // check + silent switch). No-op for Customers and for anyone already on NC999.
      if (this.$root) this.$root.$emit("jcs-return-home");

      (this.cartId = request.id),
        (this.orderId = request.orderId),
        (this.orders = request.purchaseOrders),
        (this.companyName = request.companyName),
        (this.billingCompanyName = request.plantName),
        (this.billingStreet = request.plantStreet),
        (this.billingStreet2 = request.plantStreet2),
        (this.billingCity = request.plantCity),
        (this.billingState = request.plantState),
        (this.billingPostalCode = request.plantPostalCode),
        (this.shippingCompanyName = request.shipName),
        (this.shippingStreet = request.shipStreet),
        (this.shippingStreet2 = request.shipStreet2),
        (this.shippingCity = request.shipCity),
        (this.shippingState = request.shipState),
        (this.shippingPostalCode = request.shipPostalCode),
        (this.name = request.name),
        (this.phone = request.plantPhone),
        (this.total = request.cost),
        (this.fullOrder = request);

      // Now that the order is fully recorded and the page
      // has its data, load the Frequently Ordered upsell.
      // Calling it here (rather than from mounted) avoids
      // a race against the place-order backend flow.
      this.loadUpsellInsights(request.companyId);

      // ===== AUTO-REMOVE "JUST THIS TIME" SETUP ADDS =====
      // If the cross-company reorder flow added any machines/wear parts to
      // this company "just for this order", the order is now placed — undo
      // them. Best-effort and fully isolated: it never throws or blocks this
      // success handler (the helper resolves on every path). Scoped strictly
      // to the placed order's company (falls back to localStorage.companyId,
      // both captured into placedCompanyId above).
      try {
        var cleanupCompanyId =
          this.placedCompanyId ||
          (request && request.companyId) ||
          localStorage.companyId ||
          "";
        var self = this;
        cleanupTempSetupAdds(apiService, cleanupCompanyId, function (msg, opts) {
          self.$bvToast.toast(msg, {
            title: (opts && opts.title) || "Temporary setup cleaned up",
            autoHideDelay: 8000,
            appendToast: true,
            variant: (opts && opts.variant) || "info",
            solid: true,
          });
        });
      } catch (e) {
        // Defensive only — cleanupTempSetupAdds is built not to throw.
      }
    },
    orderFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },

    // ===== FREQUENTLY ORDERED UPSELL =====

    // Pulls topParts via the same getInsights endpoint used by
    // Index_-_Search.vue's upsell panel. YTD, take=50 — the
    // displayedUpsellParts computed slices to upsellLimit for
    // the visible list. Defensive: failures are logged once
    // and the panel hides, so a temporary insights outage
    // doesn't block the success page.
    //
    // Accepts an optional explicit cid argument — pass it
    // when calling from a flow that has authoritative
    // company data (e.g. orderSuccessful's request.companyId).
    // Falls back to localStorage.companyId for compatibility
    // with any other call sites.
    loadUpsellInsights(explicitCid) {
      var cid = explicitCid || localStorage.companyId;
      if (!cid) {
        console.warn("[Success] upsell insights skipped — no companyId");
        return;
      }
      this.upsellCompanyId = cid;

      var self = this;
      apiService
        .getInsights({
          companyId: cid,
          CreateQuote: false,
          allTime: false, // YTD by default — matches dashboard / search
          take: 50,
        })
        .then(function (data) {
          var topParts = (data && data.topParts) || [];
          self.upsellParts = topParts;
          if (topParts.length === 0) {
            // Empty response. Either (a) the company has no
            // historical orders to draw top-parts from yet,
            // or (b) the just-placed order isn't indexed in
            // time. Logged here so we can spot the latter
            // case without breaking the page.
            console.info(
              "[Success] upsell insights returned no topParts for company " + cid
            );
          }
        })
        .catch(function (err) {
          console.warn("[Success] upsell insights load failed:", err);
          self.upsellParts = [];
        });
    },

    // ADD on a Frequently Ordered row: hand off to /search
    // with the part code + qty pre-loaded. Search.vue reads
    // ?code=, ?qty=, and ?allParts=true and runs its existing
    // modal flow — Order vs Quote, PO Number, confirm —
    // without forcing Success to duplicate any of that logic.
    //
    // ?allParts=true is important: by default Search.vue
    // pre-filters to wear-parts-only, which hides any part
    // that isn't classified as a wear part. Frequently
    // Ordered surfaces both wear and non-wear parts, so the
    // handoff has to disable the wear-only filter so the
    // user actually sees the part they clicked. Pattern
    // matches Head.vue's header-search bar, which uses the
    // same flag for the same reason.
    //
    // Quantity defaults to the part's totalQuantity /
    // timesOrdered average (rounded), capped at the
    // historical totalQuantity, with a floor of 1 — gives
    // the user a sensible starting point but lets them
    // adjust in the Search modal before confirming.
    handoffUpsellAdd(part) {
      if (!part || !part.partCode) return;
      // Suggested qty: the average per-order quantity, rounded
      // to the nearest whole part. If timesOrdered is 0 or
      // missing, fall back to 1.
      var times = parseInt(part.timesOrdered, 10);
      var totalQty = parseInt(part.totalQuantity, 10);
      var suggested = 1;
      if (isFinite(times) && times > 0 && isFinite(totalQty) && totalQty > 0) {
        suggested = Math.max(1, Math.round(totalQty / times));
      }
      // Build the destination query. `code` and `qty` are the
      // legacy params Search.vue's part picker has historically
      // consumed. The `modifyMachineId` / `modifyCompanyId`
      // params reuse Search.vue's existing applyModifyQuery
      // hydration path so the destination page lands with
      // Step 2 (Machine) pre-selected to whatever the user
      // had picked when they placed THIS order — saves them
      // re-selecting the machine just to add another part.
      // Without this hydration, localStorage.machineId is
      // already wiped by orderSuccessful's cleanup so the
      // destination would otherwise mount with no machine.
      var query = {
        code: part.partCode,
        qty: suggested,
        allParts: "true",
      };
      if (this.placedCompanyId) {
        query.modifyCompanyId = this.placedCompanyId;
      }
      if (this.placedMachineId) {
        query.modifyMachineId = this.placedMachineId;
      }
      this.$router.push({
        path: "/search",
        query: query,
      });
    },
  },
};
</script>

<style scoped>
/* ===== PRINT-ONLY HEADER (HIDDEN ON SCREEN) =====
   The letterhead-style banner at the top of the template
   appears ONLY in print output. Hidden by default here so
   it doesn't show in the live UI; revealed by the @media
   print rule in the non-scoped style block at the bottom
   of this file. */
.print-only-header {
    display: none;
}

/* ============================================================
   STYLES PORTED FROM OrderDetails.vue
   ============================================================
   The Sold To / Ship To info-card pattern and the order
   grand-total panel are copied verbatim from OrderDetails.vue
   so that an order placed via Success and later reviewed via
   Order Details renders identically. If OrderDetails is
   later restyled, mirror the change here to keep the two in
   sync — or extract these rules to a shared file and import
   from both components.

   Both components use <style scoped>, which prevents leakage
   to other pages but also means we can't simply share rules
   via the cascade — duplication is the trade-off.
   ============================================================ */

/* ===== INFO CARDS (Sold To + Ship To) ===== */
.info-cards-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 28px;
}

.info-card {
    flex: 1 1 0;
    min-width: 240px;
    background: #fff;
    border: 1px solid #ececec;
    border-top: 3px solid #c0392b;
    border-radius: 6px;
    padding: 16px 20px 14px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.info-card-header {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #c0392b;
    padding-bottom: 8px;
    margin-bottom: 10px;
    border-bottom: 1px dashed #d8d8d8;
}

.info-card-body {
    font-size: 13px;
    color: #555;
    line-height: 1.5;
}

.info-card-name {
    font-size: 15px;
    font-weight: 700;
    color: #222;
    margin-bottom: 6px;
}

.info-card-body p {
    margin: 0 0 2px;
}

/* ===== ORDER GRAND TOTAL =====
   Heavy black top border, centered uppercase label, large
   red amount. */
.cart-grand-total {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 18px 20px;
    margin-top: 8px;
    margin-bottom: 24px;
    background: #fff;
    border: 1px solid #e5e5e5;
    border-top: 3px solid #222;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.cart-grand-total-label {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #333;
}

.cart-grand-total-value {
    font-size: 22px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
}

@media (max-width: 575.98px) {
    .cart-grand-total {
        padding: 14px 16px;
    }
    .cart-grand-total-value {
        font-size: 18px;
    }
}

/* ============================================================
   FREQUENTLY ORDERED UPSELL PANEL
   ============================================================
   Direct port of the .upsell-panel rules in Index_-_Search.vue
   so the sidebar on Success looks visually identical to the
   right-rail Frequently Ordered card on Search. Keeping the
   CSS local (rather than promoting to global) since both
   components use <style scoped> — same trade-off as the
   info-cards block above. If either copy changes, mirror
   the change here.
   ============================================================ */
.upsell-panel {
    background: #fff;
    border: 1px solid #ececec;
    border-top: 3px solid #c0392b;
    border-radius: 6px;
    padding: 16px 18px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.upsell-header {
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px dashed #d8d8d8;
}

.upsell-title {
    margin: 0 0 4px 0;
    font-size: 18px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
    text-transform: uppercase;
}

.upsell-sub {
    margin: 0;
    font-size: 12px;
    color: #777;
    line-height: 1.4;
}

.upsell-sub strong {
    color: #444;
}

.upsell-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.upsell-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;
}

.upsell-item:last-child {
    border-bottom: none;
}

.upsell-item-main {
    flex: 1 1 auto;
    min-width: 0;
}

.upsell-item-code {
    font-size: 13px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.2px;
    margin-bottom: 2px;
}

.upsell-item-desc {
    font-size: 13px;
    color: #333;
    line-height: 1.35;
    margin-bottom: 4px;
}

.upsell-item-meta {
    font-size: 11px;
    color: #888;
    font-style: italic;
}

.upsell-item-meta strong {
    color: #555;
    font-style: normal;
}

.upsell-item-add {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #c0392b;
    background: #fff;
    border: 1.5px solid #c0392b;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
}

.upsell-item-add:hover {
    background: #c0392b;
    color: #fff;
}

.upsell-see-more {
    margin-top: 10px;
    padding-top: 12px;
    border-top: 1px dashed #e5e5e5;
    text-align: center;
}

.upsell-see-more-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background: transparent;
    border: 1px solid #ccc;
    border-radius: 4px;
    color: #444;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.upsell-see-more-btn:hover {
    background: #f3f3f3;
    border-color: #999;
}

/* ===== QUICK ACTIONS UNDER UPSELL =====
   Three secondary buttons (Check Order Status, Create New
   Order, Create New Quote) that used to be the entirety of
   the right-rail "While You Are Here" card. Kept here as
   smaller-typography stacked buttons so the user can still
   reach them, but the upsell card above is the primary focus
   now. */
.upsell-quick-actions {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.upsell-quick-btn {
    display: block;
    text-align: center;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 8px 12px;
}

/* ===== PRINT ORDER BAND =====
   Centered Print button in its own breathing-room band.
   Sits between the heading/info-cards block and the PO
   detail blocks, so the white space framing it makes the
   button feel like a deliberate action point rather than
   an inline header element. */
.print-order-band {
    padding: 32px 0;
    text-align: center;
    background: transparent;
}

.btn-print-order {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 10px 24px;
    border-radius: 6px;
    background: #c0392b;
    border: 1px solid #c0392b;
    color: #fff;
    transition: background 200ms ease, color 200ms ease,
                border-color 200ms ease, box-shadow 200ms ease;
}

.btn-print-order:hover,
.btn-print-order:focus {
    background: #000;
    color: #fff;
    border-color: #000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.btn-print-icon {
    display: inline-block;
}

/* ===== PER-ORDER PRINT SECTION =====
   Wrapper around each order group (one per PO) that gives
   the @media print rules a hook to (a) page-break between
   consecutive orders and (b) hide a section entirely when
   the user picked a subset to print. On screen it's a
   transparent passthrough — no padding, no border, no
   visual change. */
.print-order-section {
    /* Intentionally empty on screen — display:block default
       with no styling. Print rules in the non-scoped style
       block below do all the work. */
}

/* ===== PRINT ORDER MODAL =====
   Modal-scoped styles for #print-order-modal, mirroring
   the visual treatment of the Print Quote / Finalize
   modals on the quote pages so the Order and Quote flows
   feel like siblings. The .modify-restored-banner /
   .convert-* class names are reused verbatim so any future
   tweaks to the picker styling there propagate here
   without divergence — the styles below scope them to
   .print-order-modal so they don't leak globally. */
.print-order-modal .modify-restored-banner.print-order-banner {
    background: linear-gradient(135deg, #fff5f3 0%, #fff 100%);
    border: 2px solid #c0392b;
    border-radius: 10px;
    padding: 18px 22px;
    margin: 0 0 18px;
    text-align: center;
    box-shadow: 0 4px 16px rgba(192, 57, 43, 0.12),
                0 1px 3px rgba(0, 0, 0, 0.04);
}

.print-order-modal .modify-restored-title {
    font-size: 20px;
    font-weight: 800;
    color: #c0392b;
    letter-spacing: 0.3px;
    margin-bottom: 6px;
    line-height: 1.2;
}

.print-order-modal .modify-restored-body {
    font-size: 13px;
    color: #444;
    line-height: 1.5;
}

.print-order-modal .modify-restored-body strong {
    color: #222;
    font-weight: 700;
}

@media (max-width: 575.98px) {
    .print-order-modal .modify-restored-title {
        font-size: 18px;
    }
    .print-order-modal .modify-restored-banner.print-order-banner {
        padding: 14px 16px;
    }
}

.print-order-modal .convert-modal-intro {
    margin: 0 0 14px;
    font-size: 13px;
    color: #555;
}

.print-order-modal .convert-quote-list {
    margin: 0 0 12px;
    padding: 0;
}

.print-order-modal .convert-quote-row {
    padding: 10px 12px;
    background: #fafafa;
    border: 1px solid #ececec;
    border-radius: 6px;
    margin-bottom: 6px;
}

.print-order-modal .convert-quote-row:last-child {
    margin-bottom: 0;
}

.print-order-modal .convert-quote-row-grouped {
    border-left: 3px solid #c0392b;
}

.print-order-modal .convert-quote-row .custom-control-label {
    cursor: pointer;
    width: 100%;
}

.print-order-modal .convert-quote-row-main {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 2px;
}

.print-order-modal .convert-quote-row-label {
    font-size: 14px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
}

.print-order-modal .convert-quote-row-meta {
    font-size: 12px;
    color: #555;
    font-weight: 400;
}

.print-order-modal .convert-quote-row-meta strong {
    color: #222;
    font-weight: 700;
}

.print-order-modal .convert-quote-row-sub {
    font-size: 11px;
    color: #888;
    letter-spacing: 0.2px;
    text-transform: uppercase;
}

.print-order-modal .convert-quote-row-amount {
    color: #c0392b;
    font-weight: 700;
}

.print-order-modal .convert-quote-row-assemblies {
    margin-top: 4px;
    font-size: 11px;
    color: #666;
    line-height: 1.4;
}

.print-order-modal .convert-quote-row-assembly {
    padding: 1px 0;
}

.print-order-modal .print-order-row-static {
    padding: 0;
}

.print-order-modal .convert-quote-toggle-all {
    text-align: right;
    margin: 0 0 4px;
}

.print-order-modal .convert-quote-toggle-all a {
    font-size: 11px;
    font-weight: 700;
    color: #c0392b;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    cursor: pointer;
    text-decoration: none;
}

.print-order-modal .convert-quote-toggle-all a:hover {
    text-decoration: underline;
}
</style>

<!-- ============================================================
     PRINT STYLESHEET (non-scoped, global @media print)
     ============================================================
     Triggered by window.print() or Ctrl+P. Goal: the printout
     should look exactly like the success page on screen,
     just minus the page chrome and interactive widgets.

     Includes a print-only header at the top of every printed
     page (logo + Parts Department address) so the printed
     receipt is letterhead-equivalent. The header is NOT
     visible on screen (display: none in the on-screen
     stylesheet block above), only in print.

     Uses position: running() / @top-left etc. would be the
     CSS-paged-media spec way to repeat the header on every
     page, but support is patchy across browsers. Instead we
     render the header as a regular block at the top of the
     printable section — it will appear once on page 1. If
     the user wants it repeating on every page, that requires
     a heavier rewrite via thead-style positioning.

     What we hide:
       - Site header / nav / breadcrumb / footer
       - Frequently Ordered upsell + quick-action buttons
       - The Print button band itself (.no-print)
       - Third-party widget overlays (translate, accessibility,
         chat) — common id/class fragments + iframes -->
<style>
@media print {
    /* Hide chrome — bumped specificity by repeating the
       selector twice ([id="breadcrumb"]#breadcrumb pattern)
       and also targeting the visible elements via class
       fragments so even if the id-based rule loses a
       specificity battle, these get hit. Some site shells
       set display:flex !important on breadcrumb wrappers
       in their main stylesheet, which would tie a plain
       #breadcrumb { display:none !important } — repeating
       the id selector lifts our specificity above. */
    body #breadcrumb,
    body #breadcrumb *,
    div#breadcrumb,
    div#breadcrumb *,
    [id="breadcrumb"],
    [id="breadcrumb"] *,
    [class*="breadcrumb" i],
    [class*="breadcrumb" i] *,
    [id*="breadcrumb" i],
    [id*="breadcrumb" i] *,
    nav[aria-label*="breadcrumb" i],
    nav[aria-label*="Breadcrumb"],
    ol.breadcrumb,
    ul.breadcrumb,
    body header,
    body .header,
    body #header,
    body [role="banner"],
    body nav.navbar,
    body .navbar,
    body footer,
    body .footer,
    body #footer,
    body #cartSidebar,
    body .upsell-panel,
    body .upsell-quick-actions,
    body #finalizeTop,
    .no-print,
    [class*="userway"],
    [id*="userway"],
    [class*="accessibility-widget"],
    [class*="translate-widget"],
    [id*="google_translate"],
    iframe {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    /* Strip the brushed-steel body background — would print
       as a giant gray block. */
    html,
    body {
        background: #fff !important;
        background-image: none !important;
    }

    /* ===== STRIP TOP-OF-PAGE WHITESPACE =====
       App.vue sets a dynamic padding-top on body to push
       content below the fixed site header. That padding
       has no purpose in print (header is hidden) but the
       inline style stays and creates a tall blank band at
       the top of page 1. Force it to zero. */
    html,
    body,
    #app,
    #app > div,
    main,
    .main,
    [role="main"],
    .container-fluid,
    #cartSuccess {
        margin-top: 0 !important;
        padding-top: 0 !important;
    }
    body[style] {
        padding-top: 0 !important;
        margin-top: 0 !important;
    }

    /* Show the print-only header. Hidden on screen by the
       scoped block above; revealed in print here. flex
       layout puts logo on the left and address block
       right-aligned on the right. */
    .print-only-header {
        display: flex !important;
        align-items: flex-start;
        justify-content: space-between;
        padding: 0 5px 16px;
        margin: 0 0 16px;
        border-bottom: 2px solid #c0392b;
    }

    .print-only-header-logo {
        flex: 0 0 auto;
        margin-left: 5px;
    }

    .print-only-header-logo-img {
        width: 180px;
        max-width: 180px;
        height: auto;
        max-height: 80px;
        display: block;
    }

    /* Text fallback used when the SVG <img> fails to load.
       Renders large brand-red "STEELE" so the printed
       receipt still shows a brand mark when the asset
       resolution path didn't survive the print engine. */
    .print-only-header-logo-text {
        font-size: 32px;
        font-weight: 900;
        letter-spacing: 1px;
        color: #c0392b;
        line-height: 1;
        font-family: Arial, Helvetica, sans-serif;
    }

    .print-only-header-address {
        flex: 0 0 auto;
        text-align: right;
        font-size: 11px;
        line-height: 1.4;
        color: #222;
    }

    .print-only-header-address-name {
        font-weight: 700;
        font-size: 12px;
        margin-bottom: 2px;
    }

    /* Doc identifier line above the Parts Department block.
       Brand-red + larger weight so it reads as the headline
       of the printed page — recipient can tell at a glance
       which Order/Quote/Acknowledgement they're looking at.
       Color-adjust hint forces the red through browsers'
       default print color-strip (which the rest of this
       stylesheet already uses on red elements). */
    .print-only-header-doc-id {
        font-weight: 700;
        font-size: 14px;
        color: #c0392b;
        margin-bottom: 6px;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    /* ===== INPUT-AS-TEXT IN PRINT =====
       Disabled <input> elements (e.g. the locked CART QTY
       input on Success.vue's parts table) carry intrinsic
       browser-default widths that override table-cell
       sizing and push other columns out of alignment with
       the header row. Strip the input chrome and intrinsic
       width so the value renders inline as text — same
       width as the other plain-text cells in the column.
       Defensive on files that don't currently use inputs in
       tables; harmless if no inputs are present. */
    .assembly-table-wrap input[disabled],
    .assembly-table-wrap input[readonly],
    .POOrders input[disabled],
    .POOrders input[readonly],
    .success-qty,
    .success-qty input {
        border: 0 !important;
        background: transparent !important;
        padding: 0 !important;
        margin: 0 !important;
        width: auto !important;
        max-width: none !important;
        min-width: 0 !important;
        display: inline !important;
        -webkit-appearance: none !important;
        appearance: none !important;
        color: inherit !important;
        font: inherit !important;
        box-shadow: none !important;
        outline: 0 !important;
    }

    /* Upsell column was 5/12 of the row; with it hidden,
       expand the order info column to full width. */
    #cartSuccess .col-lg-7 {
        flex: 0 0 100% !important;
        max-width: 100% !important;
    }

    /* ===== TIGHT TYPOGRAPHY FOR PRINT =====
       Screen sizes are generous; for print we tighten
       everything so a typical order fits on one or two
       pages. Base font drops to 10px with line-height
       1.35 — most page elements inherit this. */
    body {
        font-size: 10px !important;
        line-height: 1.35 !important;
    }
    #cartSuccess h1 {
        font-size: 18px !important;
        margin: 0 0 4px !important;
    }
    #cartSuccess h2 {
        font-size: 13px !important;
        margin: 0 0 4px !important;
    }
    #cartSuccess p {
        font-size: 10px !important;
        line-height: 1.35 !important;
        margin: 0 0 2px !important;
    }
    #cartSuccess .info-card,
    #cartSuccess .info-card-body,
    #cartSuccess .info-card-name,
    #cartSuccess .info-card-meta,
    #cartSuccess .info-card-header {
        font-size: 10px !important;
    }
    #cartSuccess .info-card {
        padding: 8px 10px !important;
    }
    #cartSuccess .info-cards-row {
        gap: 8px !important;
        margin-top: 8px !important;
    }
    #finalizeTop {
        padding-top: 8px !important;
        padding-bottom: 8px !important;
    }
    #finalizeTop .container {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }
    #finalizeTop .row {
        margin-bottom: 8px !important;
    }
    .POOrders {
        margin: 6px 0 !important;
    }
    .POOrders .container {
        padding-top: 4px !important;
        padding-bottom: 4px !important;
    }
    .ponumber {
        font-size: 11px !important;
        margin: 4px 0 !important;
    }
    .assemblyName {
        font-size: 10px !important;
        margin: 0 !important;
    }
    .assemblyDescription {
        font-size: 9px !important;
        margin: 0 !important;
    }
    .POOrders table {
        font-size: 9px !important;
        /* Match the .assembly-table-wrap table rule used by
           the other 3 print views: force the table to span
           the full content width so columns spread evenly
           rather than crowding into the left edge with a
           wide blank gap on the right. table-layout: auto
           lets the browser size each column proportionally
           to its content. */
        width: 100% !important;
        table-layout: auto !important;
    }
    .POOrders th,
    .POOrders td {
        /* Padding bumped from 3px/5px to 4px/8px so cells
           breathe a bit more horizontally now that the table
           occupies full page width. */
        padding: 4px 8px !important;
        font-size: 9px !important;
    }
    .cart-grand-total {
        margin-top: 8px !important;
        padding: 8px 12px !important;
    }
    .cart-grand-total-label {
        font-size: 10px !important;
    }
    .cart-grand-total-value {
        font-size: 14px !important;
    }

    /* ===== PAGE BREAK CONTROL =====
       Don't put page-break-inside: avoid on the wider
       .POOrders block — it can be tall enough that the
       engine pushes the whole block to the next page
       when it doesn't fit, leaving a white gap. Apply
       avoid only to small atoms (info cards, grand
       total) so content flows naturally. */
    .info-card,
    .cart-grand-total {
        page-break-inside: avoid;
        break-inside: avoid;
    }

    /* b-table stacked-mode override — print viewport trips
       the lg breakpoint, breaking table layout into
       label/value stacks. Force back to row mode. */
    .b-table.b-table-stacked-lg > tbody > tr,
    .b-table.b-table-stacked-md > tbody > tr,
    .b-table.b-table-stacked-sm > tbody > tr {
        display: table-row !important;
    }
    .b-table.b-table-stacked-lg > tbody > tr > td,
    .b-table.b-table-stacked-md > tbody > tr > td,
    .b-table.b-table-stacked-sm > tbody > tr > td {
        display: table-cell !important;
    }
    .b-table.b-table-stacked-lg > thead,
    .b-table.b-table-stacked-md > thead,
    .b-table.b-table-stacked-sm > thead {
        display: table-header-group !important;
    }
    .b-table.b-table-stacked-lg > tbody > tr > [data-label]::before,
    .b-table.b-table-stacked-md > tbody > tr > [data-label]::before,
    .b-table.b-table-stacked-sm > tbody > tr > [data-label]::before {
        display: none !important;
    }

    /* ===== PER-ORDER PAGE BREAKS =====
       Each .print-order-section past the first gets a
       page-break-before so every Order # block starts on
       its own printed page with its own letterhead at the
       top. The first section inherits the page-top
       position naturally — no break needed there. */
    .print-order-section + .print-order-section {
        page-break-before: always;
        break-before: page;
    }

    .print-order-section {
        page-break-inside: auto;
        break-inside: auto;
    }

    /* ===== SELECTIVE HIDE FOR SUBSET PRINTING =====
       confirmPrint() flips printingSelectedOnly=true and
       Vue toggles `print-order-hidden` onto every section
       whose group.number isn't in selectedOrdersForPrint.
       The display: none here removes those sections from
       the printed output entirely — including their
       per-order letterhead — so a 3-of-5 print only emits
       the picked 3 with correct page breaks between them. */
    .print-order-hidden,
    .print-order-hidden * {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    /* Page setup. */
    @page {
        margin: 0.5in;
    }

    /* ===== FORCE DARK TEXT FOR PRINT READABILITY =====
       Browser print engines lighten text by default to save
       ink. Combined with the various screen-only light-gray
       colors (#555, #666, #777, #888, #aaa) sprinkled across
       the screen styles, the printed output ends up muted
       and hard to read. Force every text node to full black
       AND bump font-weight slightly so thin glyphs don't
       disappear at small print font sizes. */
    *,
    *::before,
    *::after {
        color: #000 !important;
    }

    body,
    body p,
    body span,
    body td,
    body th,
    body div {
        font-weight: 500 !important;
    }

    /* ===== AGGRESSIVE GLOBAL DECORATIVE RESET =====
       Earlier attempts to strip borders only on specific
       containers didn't eliminate the vertical gray lines
       running down the body of printed pages. Edge browser
       in particular prints subtle borders by default —
       different browsers behave differently here.

       Brute-force approach: wipe ALL backgrounds, borders,
       and shadows on EVERY element AND every pseudo-element.
       The pseudo-element coverage matters — App.vue (or a
       global stylesheet) sets up `body::before` and
       `body::after` to render the brushed-steel side rails
       around the page, and a universal `*` selector by
       itself doesn't catch those. Without this rule the
       rails stay visible in print and produce vertical bars
       running the full page height.

       border-color: transparent (rather than border-width:
       0) preserves layout — elements that used borders for
       spacing keep their box geometry, only the visible
       line goes away. */
    *,
    *::before,
    *::after {
        background: transparent !important;
        background-color: transparent !important;
        background-image: none !important;
        border-color: transparent !important;
        box-shadow: none !important;
        outline: none !important;
    }

    /* Explicit body pseudo-element kill — App.vue's brushed-
       steel side rails attach here. Belt-and-suspenders with
       the universal rule above; some browsers need the
       targeted nudge to actually drop the rendered content. */
    body::before,
    body::after,
    html::before,
    html::after {
        display: none !important;
        content: none !important;
    }

    /* ===== HIDE FLOATING UTILITY TOOLBAR =====
       Head.vue mounts a fixed-position #utility-toggle-stack
       in the bottom-right of every page, containing three
       toggles: Report-a-Bug, font-size (Aa), and translate
       (globe). These are screen-only UI affordances; in a
       printed receipt they're noise. */
    #utility-toggle-stack,
    #utility-toggle-stack *,
    .util-toggle,
    .util-toggle-bug,
    .util-toggle-font,
    .util-toggle-translate,
    .util-icon,
    .util-label,
    .util-close,
    .util-body,
    #google-translate-floating,
    #google_translate_element {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        width: 0 !important;
        overflow: hidden !important;
    }

    /* Hide any horizontal-rule elements which would otherwise
       print as visible divider lines. */
    hr {
        display: none !important;
    }

    /* ===== RESTORE INTENTIONAL CHROME =====
       Now re-establish the visual elements that DO belong
       in print:
         - Data table cell borders carry data structure
         - print-only-header gets its red bottom rule (the
           branded divider between header and content)
         - Red brand accents stay red */
    table,
    .assembly-table-wrap table {
        border-collapse: collapse !important;
    }
    th,
    td,
    .assembly-table-wrap th,
    .assembly-table-wrap td {
        border: 1px solid #888 !important;
    }

    .print-only-header {
        border-bottom: 2px solid #c0392b !important;
    }

    /* Targeted color preservation on the red brand elements
       so they print in their declared color instead of the
       browser's default auto-strip. */
    .print-only-header,
    .print-only-header *,
    .print-only-header-logo-img,
    .print-only-header-logo-text,
    .print-only-header-logo-text *,
    [class*="-red"],
    [class*="brand-red"] {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    /* Restore red on the brand wordmark fallback + any
       explicit red accents the page intentionally carries.
       Order matters — these rules must come after the
       universal `*` color override above. */
    .print-only-header-logo-text,
    .print-only-header-logo-text *,
    [class*="-red"],
    [class*="brand-red"] {
        color: #c0392b !important;
    }
}
</style>